// src/utils/tokenRefreshService.ts
import jwtDecode from 'jwt-decode';
import TokenManager from './tokenManager';
import { LOGIN, P2P_STATIC } from '../pages/routes';
import { BASE_URL, REGENRATE_TOKEN_API } from '../constants';
import snackbar from './snackbar';
import { swAuth } from './serviceWorkerAuth';
import { LOG_STYLES } from './consoleColors';
import {
  LAST_PUSH_TOKEN_KEY,
  REMOVE_PUSH_TOKEN
} from '@/constants/pushMessages';
import { closeInboxSocket } from '@/services/notifications/inboxSocket';

/**
 * TokenRefreshService - Manages automatic token refresh and session restoration
 *
 * Features:
 * 1. Auto-refresh: Refreshes token 10 minutes before expiry
 * 2. Proactive refresh: Refreshes when tab becomes visible if < 5 min left
 * 3. Session restore: Attempts to restore session from HttpOnly cookie on browser restart
 * 4. Singleton pattern: Prevents concurrent refresh calls
 */
class TokenRefreshService {
  private refreshPromise: Promise<string> | null = null;

  private refreshTimer: NodeJS.Timeout | null = null;

  private isLoggingOut = false;

  private initPromise: Promise<void> | null = null;

  private visibilityHandler: (() => void) | null = null;

  private logoutUnsubscribe: (() => void) | null = null;

  // Refresh 10 minutes before expiry
  private readonly REFRESH_BUFFER_MS = 10 * 60 * 1000; // 10 minutes

  // Proactive refresh threshold (when tab becomes visible)
  private readonly PROACTIVE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Schedule preemptive token refresh
   * Calculates time until token expires and sets timeout to refresh 10 min before
   */
  async scheduleRefresh() {
    this.clearRefreshTimer();

    const token = await TokenManager.getAccessToken();
    if (!token) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] No token to schedule refresh for',
          LOG_STYLES.verbose
        );
      }
      return;
    }

    try {
      const decoded: any = jwtDecode(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      const refreshIn = expiresAt - now - this.REFRESH_BUFFER_MS;

      if (refreshIn > 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Scheduling refresh in',
            LOG_STYLES.verbose,
            Math.floor(refreshIn / 1000 / 60),
            'minutes'
          );
        }

        this.refreshTimer = setTimeout(() => {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenService] Scheduled refresh triggered',
              LOG_STYLES.info
            );
          }
          this.refresh()
            .then(() => {
              if (process.env.NODE_ENV !== 'production') {
                console.log(
                  '%c[TokenService] ✅ Scheduled refresh succeeded',
                  LOG_STYLES.success
                );
              }
              this.scheduleRefresh(); // Schedule next refresh
            })
            .catch((error) => {
              if (process.env.NODE_ENV !== 'production') {
                console.error(
                  '%c[TokenService] ❌ Scheduled refresh failed:',
                  LOG_STYLES.error,
                  error
                );
              }
              // Silent fail - will retry on next API request
            });
        }, refreshIn);
      } else {
        // Token already expired - refresh immediately
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Token already expired, refreshing immediately',
            LOG_STYLES.warning
          );
        }
        this.refresh()
          .then(() => this.scheduleRefresh())
          .catch(() => {
            // Will be handled on next API call
          });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '%c[TokenService] ❌ Schedule error:',
          LOG_STYLES.error,
          error
        );
      }
    }
  }

  /**
   * Clear refresh timer
   */
  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] Refresh timer cleared',
          LOG_STYLES.verbose
        );
      }
    }
  }

  /**
   * Refresh access token using HttpOnly refresh cookie
   *
   * How it works:
   * 1. POST to /token/reset with credentials: 'include'
   * 2. Server validates HttpOnly refresh token cookie
   * 3. Server returns new access token
   * 4. Store new token in all layers (memory + SW + device_context)
   *
   * Uses singleton pattern to prevent concurrent refreshes
   */
  async refresh(): Promise<string> {
    // Return existing promise if refresh already in progress
    if (this.refreshPromise) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] Refresh already in progress, waiting...',
          LOG_STYLES.verbose
        );
      }
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh();

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual refresh API call
   */
  // eslint-disable-next-line class-methods-use-this
  private async _performRefresh(): Promise<string> {
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenService] Refreshing token...', LOG_STYLES.info);
    }

    const response = await fetch(`${BASE_URL}${REGENRATE_TOKEN_API}`, {
      method: 'POST',
      credentials: 'include', // ✅ CRITICAL: Sends HttpOnly refresh token cookie
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Get error details from server
      let errorMessage = `Refresh failed: ${response.status}`;
      let serverMessage = '';

      try {
        const errorData = await response.json();
        serverMessage = errorData.message || '';
        errorMessage = serverMessage || errorMessage;

        if (process.env.NODE_ENV !== 'production') {
          console.error(
            '%c[TokenService] ❌ Server error:',
            LOG_STYLES.error,
            errorData
          );
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            '%c[TokenService] ❌ Could not parse error response',
            LOG_STYLES.error
          );
        }
      }

      const error: any = new Error(errorMessage);
      error.status = response.status;
      error.serverMessage = serverMessage;
      throw error;
    }

    const data = await response.json();
    const { accessToken } = data.data;

    // Store new access token in all layers
    await TokenManager.set({ accessToken });

    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenService] ✅ Token refreshed successfully',
        LOG_STYLES.success
      );
    }
    return accessToken;
  }

  /**
   * Initialize on app load
   * Called once from App.tsx useEffect
   *
   * Flow:
   * 1. Wait for Service Worker to be ready
   * 2. Check if token exists (memory or SW)
   * 3. If yes → Schedule refresh, setup listeners
   * 4. If no → Check for previous session (device_context)
   * 5. If previous session → Try to restore from HttpOnly cookie
   */

  async initialize() {
    // Cache the promise — multiple callers get the same init
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize() {
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenService] Initializing...', LOG_STYLES.info);
    }

    // Wait for Service Worker
    try {
      await swAuth.init();
      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[TokenService] Service Worker ready', LOG_STYLES.info);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] SW init failed:',
          LOG_STYLES.warning,
          error
        );
      }
    }

    // Check for actual token first
    const hasToken = await TokenManager.hasToken();

    if (hasToken) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] ✅ Valid token found',
          LOG_STYLES.success
        );
      }
      this.scheduleRefresh();

      // Setup listeners AFTER confirming token
      this.setupVisibilityListener();
      this.setupLogoutListener();
      return;
    }

    // No token in memory/SW - check if user had previous session
    if (!TokenManager.hadPreviousSession()) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] No previous session found',
          LOG_STYLES.verbose
        );
      }
      return;
    }

    // Had previous session - try to restore from HttpOnly cookie
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenService] Previous session found, attempting restore...',
        LOG_STYLES.info
      );
    }

    try {
      await this.refresh();
      this.scheduleRefresh();

      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[TokenService] ✅ Session restored', LOG_STYLES.success);
      }

      this.setupVisibilityListener();
      this.setupLogoutListener();
    } catch (error: any) {
      const isExpired = error.status === 401 || error.status === 400;

      if (isExpired) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Session expired:',
            LOG_STYLES.warning,
            error.serverMessage || error.message
          );
        }
        // Clear device_context - session cannot be restored
        localStorage.removeItem('device_context');

        // Don't show error snackbar on page load - just redirect to login
        // User will see login page naturally via PrivateRoute
      } else {
        // Network error or other issue
        // eslint-disable-next-line no-lonely-if
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            '%c[TokenService] ❌ Session restore failed:',
            LOG_STYLES.error,
            error.message
          );
        }
      }
    }
  }

  /**
   * Check token and refresh if needed
   * Called when tab becomes visible after being hidden
   *
   * Logic:
   * 1. Check if token exists
   * 2. If no token + had previous session → Try to restore
   * 3. If has token → Check expiry
   * 4. If < 5 min left → Proactive refresh
   */
  async checkAndRefreshIfNeeded() {
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenService] Checking token status...',
        LOG_STYLES.verbose
      );
    }

    const hasToken = await TokenManager.hasToken();

    if (!hasToken) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[TokenService] No token found', LOG_STYLES.verbose);
      }

      // Try to restore if had previous session
      if (TokenManager.hadPreviousSession()) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Attempting to restore session...',
            LOG_STYLES.info
          );
        }
        try {
          await this.refresh();
          this.scheduleRefresh();
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenService] ✅ Session restored',
              LOG_STYLES.success
            );
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenService] Session restore failed',
              LOG_STYLES.warning
            );
          }
          this.logout(true, 'نشست شما منقضی شده است.');
        }
      }
      return;
    }

    // Have token - check if expiring soon
    try {
      const token = await TokenManager.getAccessToken();
      if (!token) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Token null after hasToken check',
            LOG_STYLES.verbose
          );
        }
        return;
      }

      const decoded: any = jwtDecode(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      const timeLeft = expiresAt - now;

      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[TokenService] Token check:', LOG_STYLES.verbose, {
          expiresIn: `${Math.floor(timeLeft / 1000 / 60)} minutes`,
          expiresAt: new Date(expiresAt).toLocaleString()
        });
      }

      // If less than 5 minutes left, refresh proactively
      if (timeLeft < this.PROACTIVE_REFRESH_THRESHOLD_MS) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] Token expiring soon, refreshing...',
            LOG_STYLES.info
          );
        }
        await this.refresh();
        this.scheduleRefresh();
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenService] ✅ Token refreshed proactively',
            LOG_STYLES.success
          );
        }
      } else if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] Token still valid, no refresh needed',
          LOG_STYLES.verbose
        );
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '%c[TokenService] ❌ Token check failed:',
          LOG_STYLES.error,
          error
        );
      }
    }
  }

  /**
   * Setup visibility change listener
   * Monitors when tab becomes visible and checks if token needs refresh
   */
  private setupVisibilityListener() {
    if (typeof document === 'undefined') return;
    if (this.visibilityHandler) return; // already attached

    this.visibilityHandler = async () => {
      if (document.visibilityState === 'visible') {
        await this.checkAndRefreshIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Setup Service Worker logout listener
   * Listens for logout events from SW
   */
  private setupLogoutListener() {
    if (this.logoutUnsubscribe) return; // already attached

    this.logoutUnsubscribe = swAuth.onLogout((reason) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] SW logout event:',
          LOG_STYLES.info,
          reason
        );
      }
      this.logout(true, 'نشست شما منقضی شده است.');
    });
  }

  /**
   * Best-effort push-token removal on logout (backend doc §3c).
   * - Runs BEFORE TokenManager.clear() so the access token + push token still exist.
   * - Raw fetch (not the RTK mutation) to avoid the store→api→this circular import,
   *   consistent with _performRefresh().
   * - Sync memory read for the JWT; fire-and-forget; never awaited, never blocks logout.
   * - If the token/JWT is already gone (expired-session logout), we skip it — the
   *   server also removes this device by fingerprint on GET /logout, so push still stops.
   */
  // eslint-disable-next-line class-methods-use-this
  private removePushTokenBestEffort() {
    try {
      const pushToken = localStorage.getItem(LAST_PUSH_TOKEN_KEY);
      if (!pushToken) return;

      const { accessToken } = TokenManager.get(); // sync memory read
      if (!accessToken) return;
      localStorage.removeItem(LAST_PUSH_TOKEN_KEY);
      fetch(`${BASE_URL}${REMOVE_PUSH_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ token: pushToken })
      }).catch(() => {
        // best-effort — server removes by fingerprint on logout anyway
      });
      // note: lastPushToken is cleared by TokenManager.clear() right after this
    } catch {
      // push cleanup must NEVER break logout
    }
  }

  /**
   * Stop refresh timer
   */
  stop() {
    this.clearRefreshTimer();

    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.logoutUnsubscribe) {
      this.logoutUnsubscribe();
      this.logoutUnsubscribe = null;
    }

    // Allow re-init after stop (HMR, error recovery)
    this.initPromise = null;
  }

  /**
   * Logout user
   * Clears all tokens and redirects to login page
   */
  /**
   * `redirectTo` is where the app reloads to once the session is gone: the
   * login page by default (an expired session, or desktop, whose home needs
   * sign-in). A user who logs out from the mobile profile sheet goes to the
   * home page instead and stays there as a guest.
   */
  logout(
    showSnackbar = false,
    message = 'لطفاً دوباره وارد شوید.',
    redirectTo: string = LOGIN
  ) {
    if (this.isLoggingOut) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenService] Already logging out, skipping',
          LOG_STYLES.verbose
        );
      }
      return;
    }

    this.isLoggingOut = true;
    this.removePushTokenBestEffort();
    // The socket authenticates in the handshake with the token that is about
    // to be cleared, so it is closed here rather than left to reconnect on a
    // dead session.
    closeInboxSocket();

    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenService] Logging out...', LOG_STYLES.info);
    }

    this.stop();
    TokenManager.clear();

    if (showSnackbar) {
      snackbar.error(message, { autoHideDuration: 3000 });
    }

    setTimeout(() => {
      if (window.location.pathname.startsWith(P2P_STATIC)) {
        window.location.reload();
      } else {
        window.location.href = redirectTo;
      }
    }, 2000);
  }
}

export const tokenRefreshService = new TokenRefreshService();
